import {
    createContext,
    Dispatch,
    FC,
    ReactNode,
    SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';

import { PreparedVariation, VariantMatrix } from '@telialt/models/src/EshopModel';
import { DDComponent } from '@telialt/utils/src/datadog/setup';
import { useLogError } from '@telialt/utils/src/datadog/useLogError';

import { GOOGLE_MAPS } from '../constants';
import { useRetailsMapSearch } from '../hooks/useRetailsMapSearch';
import { City, RetailsData, Salon, WeekendValue } from '../model';
import { getEnrichedCities, getFilteredCities, getFirstProductVariation, getSortedCities } from '../utils';

type RetailsContextValue = {
    baseProductCode?: string;
    data?: RetailsData;
    zoom: number;
    position: google.maps.LatLngLiteral;
    allCities: City[];
    filteredCities: City[];
    selectedCity?: City;
    selectedSalon?: Salon;
    selectCity: (city?: City) => void;
    selectSalon: (salon?: Salon) => void;
    animatedSalonCardId?: string;
    animatedSalonMarkerId?: string;
    startSalonMarkerAnimation: (salonId: string) => void;
    stopSalonMarkerAnimation: (salonId: string) => void;
    isMobileMapVisible: boolean;
    isHiddenFromScreenReader?: boolean;
    setIsMobileMapVisible: (isVisible: boolean) => void;
    updateWeekendFilter: (day: WeekendValue, isEnabled: boolean) => void;
    updateVariantFilter: (variation: PreparedVariation) => void;
    isLoading?: boolean;
    setSelection: Dispatch<
        SetStateAction<{
            cityId?: string;
            salonId?: string;
            tick: number;
        }>
    >;
    setZoom: Dispatch<SetStateAction<number>>;
    setPosition: Dispatch<
        SetStateAction<{
            lat: number;
            lng: number;
        }>
    >;
    selection: { cityId?: string; salonId?: string; tick: number };
};

const RetailsContext = createContext<RetailsContextValue>(null);

type ProviderProps = {
    baseProductCode?: string;
    children: ReactNode;
    onSelectionChange?: ({ city, salon }: { city: City; salon: Salon }) => void;
};

export const RetailsContextProvider: FC<ProviderProps> = ({ baseProductCode, children, onSelectionChange }) => {
    const { data, error, isLoading } = useRetailsMapSearch(baseProductCode);
    useLogError(error, DDComponent.EshopProductCore);

    const [zoom, setZoom] = useState(GOOGLE_MAPS.zoom.map);
    const [position, setPosition] = useState({ lat: GOOGLE_MAPS.lat, lng: GOOGLE_MAPS.lng });

    const [selection, setSelection] = useState<RetailsContextValue['selection']>({ tick: 0 });

    const [animatedSalonCardId, setAnimatedSalonCardId] = useState<string>();
    const [animatedSalonMarkerId, setAnimatedSalonMarkerId] = useState<string>();

    const [isMobileMapVisible, setIsMobileMapVisible] = useState(false);

    const [weekendFilter, setWeekendFilter] = useState<Record<WeekendValue, boolean>>({
        saturday: false,
        sunday: false,
    });
    const [variationFilter, setVariationFilter] = useState<PreparedVariation>();

    const sortedCities = useMemo(() => getSortedCities(data?.retails || []), [data?.retails]);
    const allCities = useMemo(() => getEnrichedCities(sortedCities), [sortedCities]);
    const filteredCities = useMemo(
        () => getEnrichedCities(getFilteredCities({ cities: sortedCities || [], weekendFilter, variationFilter })),
        [sortedCities, variationFilter, weekendFilter],
    );

    const selectSalon = useCallback(
        (salon?: Salon) => {
            setAnimatedSalonMarkerId(undefined);

            if (salon && salon.id === selection.salonId) {
                setAnimatedSalonCardId((prevSalonId) => (prevSalonId !== salon.id ? salon.id : undefined));
            }

            setSelection((prevState) => ({
                tick: prevState.tick + 1,
                cityId: salon ? salon.cityId : prevState.cityId,
                salonId: salon?.id,
            }));

            onSelectionChange?.({
                city: allCities.find(({ id }) => id === (salon ? salon.cityId : selection.cityId)),
                salon,
            });
        },
        [allCities, onSelectionChange, selection.cityId, selection.salonId],
    );

    const selectCity = useCallback(
        (city?: City) => {
            if (city?.salons?.length > 1 && isMobileMapVisible === false) {
                setZoom(GOOGLE_MAPS.zoom.city);
            }

            setSelection((prevState) => ({
                tick: prevState.tick + 1,
                cityId: city?.id,
                salonId: city && city.salons?.length === 1 ? city.salons[0].id : undefined,
            }));

            onSelectionChange?.({
                city,
                salon: city && city.salons?.length === 1 ? city.salons[0] : undefined,
            });
        },
        [onSelectionChange, isMobileMapVisible],
    );

    useEffect(() => {
        if (selection.salonId && selection.cityId) {
            const salon = allCities
                .find(({ id }) => id === selection.cityId)
                ?.salons.find(({ id }) => id === selection.salonId);

            setZoom(GOOGLE_MAPS.zoom.salon);
            setPosition({ lat: salon.lat, lng: salon.lng });
        } else if (selection.cityId) {
            const city = allCities.find(({ id }) => id === selection.cityId);

            setZoom(GOOGLE_MAPS.zoom.city);
            setPosition({ lat: city.lat, lng: city.lng });
        }
    }, [allCities, selection]);

    const selectedCity = useMemo(
        () => filteredCities.find(({ id }) => id === selection.cityId),
        [filteredCities, selection.cityId],
    );
    const selectedSalon = useMemo(() => {
        if (selection.salonId) {
            return selectedCity?.salons?.find(({ id }) => id === selection.salonId);
        } else if (selectedCity?.salons?.length === 1) {
            return selectedCity.salons[0];
        } else {
            return undefined;
        }
    }, [selectedCity?.salons, selection.salonId]);

    const startSalonMarkerAnimation = useCallback((salonId: string) => {
        setAnimatedSalonMarkerId(salonId);
    }, []);

    const stopSalonMarkerAnimation = useCallback((salonId: string) => {
        setAnimatedSalonMarkerId((prevSalonId) => (prevSalonId === salonId ? undefined : prevSalonId));
    }, []);

    const updateWeekendFilter = useCallback((day: WeekendValue, isEnabled: boolean) => {
        setWeekendFilter((prevFilter) => ({ ...prevFilter, [day]: isEnabled }));
    }, []);

    const updateVariantFilter = useCallback((variation?: PreparedVariation) => {
        setVariationFilter(variation);
    }, []);

    useEffect(() => {
        if (!baseProductCode) {
            return;
        }

        updateVariantFilter(getFirstProductVariation(data?.variantMatrix));
    }, [baseProductCode, data?.variantMatrix, updateVariantFilter]);

    const value: RetailsContextValue = useMemo(
        () => ({
            baseProductCode,
            data,
            zoom,
            setZoom,
            setPosition,
            position,
            selectedCity,
            selectedSalon,
            allCities,
            filteredCities,
            selectCity,
            selectSalon,
            setSelection,
            selection,
            animatedSalonCardId,
            animatedSalonMarkerId,
            startSalonMarkerAnimation,
            stopSalonMarkerAnimation,
            isMobileMapVisible,
            setIsMobileMapVisible,
            updateWeekendFilter,
            updateVariantFilter,
            isLoading,
        }),
        [
            baseProductCode,
            data,
            zoom,
            position,
            selectedCity,
            selectedSalon,
            selection,
            allCities,
            filteredCities,
            selectCity,
            selectSalon,
            animatedSalonCardId,
            animatedSalonMarkerId,
            startSalonMarkerAnimation,
            stopSalonMarkerAnimation,
            isMobileMapVisible,
            updateWeekendFilter,
            updateVariantFilter,
            isLoading,
        ],
    );

    return <RetailsContext.Provider value={value}>{children}</RetailsContext.Provider>;
};

const noop = () => undefined;

export const RetailsPlaceholderContextProvider: FC<ProviderProps & { variantMatrix: VariantMatrix[] }> = ({
    baseProductCode,
    variantMatrix,
    children,
}) => (
    <RetailsContext.Provider
        value={{
            isHiddenFromScreenReader: true,
            baseProductCode,
            data: {
                variantMatrix,
                apiKey: null,
            },
            allCities: [],
            animatedSalonCardId: null,
            filteredCities: [],
            isMobileMapVisible: true,
            position: undefined,
            selectCity: noop,
            selectSalon: noop,
            setIsMobileMapVisible: noop,
            startSalonMarkerAnimation: noop,
            stopSalonMarkerAnimation: noop,
            updateVariantFilter: noop,
            updateWeekendFilter: noop,
            setSelection: noop,
            setZoom: noop,
            setPosition: noop,
            selection: { tick: 0 },
            zoom: 0,
        }}
    >
        {children}
    </RetailsContext.Provider>
);

export const useRetailsContext = () => {
    const context = useContext(RetailsContext);

    if (!context) {
        throw new Error('useRetailsContext must be used within RetailsContextProvider');
    }

    return context;
};
